import log from 'loglevel';
import { initializeApp, FirebaseApp, getApp } from 'firebase/app';
import { doc, query, where, limit, orderBy, DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { collection, onSnapshot, getFirestore, Firestore, getDocs, writeBatch, updateDoc, serverTimestamp } from "firebase/firestore";
import { getAuth, Auth, onAuthStateChanged, signOut } from 'firebase/auth';

import { AppConfigs } from '@/app.configs';
import { IFirebaseConfig } from '@/interfaces/IFirebaseConfig';
import { isUserLoggedIn } from '@/utils/utils';

import { NotificationType } from '@/constants/NotificationType';
import INotification from '@/interfaces/Notifications/INotification';
import OfferNotification from '@/interfaces/Notifications/IOfferNotification';
import IPayoutNotification from '@/interfaces/Notifications/IPayoutNotification';
import OfferWallNotification from '@/interfaces/Notifications/IOfferWallNotification';
import InHouseTaskNotification from '@/interfaces/Notifications/InHouseTaskNotification';
import ISpinTheWheelNotification from '@/interfaces/Notifications/ISpinTheWheelNotification';

/**
 * NotificationService class handles all notification-related operations
 * including Firebase authentication, Firestore operations, and notification mapping.
 */
class NotificationServiceClass {
    private firebaseApp: FirebaseApp;
    private firestore: Firestore;
    private auth: Auth;
    private isUserAuthenticated: boolean = false;
    private authStateUnsubscribe: (() => void) | null = null;

    constructor() {
        // Initialize Firebase app
        try {
            this.firebaseApp = getApp();
            log.info("Using existing Firebase app instance");
        } catch (error) {
            log.info("Creating new Firebase app instance");
            this.firebaseApp = initializeApp(AppConfigs.notificationFirebaseConfig);
        }

        // Initialize Firestore and Auth
        this.firestore = getFirestore(this.firebaseApp);
        this.auth = getAuth(this.firebaseApp);

        // Check if the user is logged in to the application
        if (isUserLoggedIn()) {
            // Set up auth state listener only if the user is logged in
            this.setupAuthStateListener();
        } else {
            // If user is not logged in but Firebase has an authenticated user, sign them out
            const currentUser = this.auth.currentUser;
            if (currentUser) {
                log.warn("Firebase user exists but application user is not logged in. Signing out from Firebase.");
                signOut(this.auth).catch(error => {
                    log.error("Error signing out from Firebase:", error);
                });
            }
        }
    }
    

    /**
     * Sets up the Firebase authentication state listener
     */
    private setupAuthStateListener(): void {
        // Clear any existing listener
        if (this.authStateUnsubscribe) {
            this.authStateUnsubscribe();
        }

        // Set up new listener
        this.authStateUnsubscribe = onAuthStateChanged(this.auth, (user) => {
            // Only consider the user authenticated if they are logged in to the application
            // and Firebase has an authenticated user
            const appLoggedIn = isUserLoggedIn();
            this.isUserAuthenticated = !!user && appLoggedIn;
            
            if (user && appLoggedIn) {
                log.info(`Firebase user authenticated: ${user.uid}`);
            } else if (user && !appLoggedIn) {
                log.warn("Firebase user exists but application user is not logged in. Ignoring Firebase authentication.");
                // Consider signing out from Firebase here if the app user is not logged in
                this.isUserAuthenticated = false;
            } else {
                log.warn("Firebase user not authenticated");
            }
        });
    }

    /**
     * Checks if the user is authenticated with Firebase
     * @returns A promise that resolves to true if the user is authenticated, false otherwise
     */
    public async checkUserAuthentication(): Promise<boolean> {
        return new Promise((resolve) => {
            if (this.isUserAuthenticated) {
                resolve(true);
                return;
            }

            // Wait for authentication state to change
            const unsubscribe = onAuthStateChanged(this.auth, (user) => {
                unsubscribe(); // Unsubscribe immediately after first callback
                this.isUserAuthenticated = !!user;
                resolve(this.isUserAuthenticated);
            });
        });
    }

    /**
     * Sets up a real-time listener for notifications
     * @param userId - The user ID to fetch notifications for
     * @param callback - The callback function to execute with the notifications data
     * @returns A promise that resolves to the unsubscribe function
     */
    public async getNotificationsListener(userId: number, callback: (notifications: INotification[]) => void): Promise<(() => void)> {
        try {
            // Check if user is authenticated
            const isAuthenticated = await this.checkUserAuthentication();
            if (!isAuthenticated) {
                log.error("Cannot set up notifications listener: User not authenticated");
                return () => {}; // Return empty function as unsubscribe
            }
            
            const currentUser = this.auth.currentUser;
            log.info(`Setting up notifications listener for user ID: ${userId} with auth UID: ${currentUser?.uid || 'unknown'}`);
            
            const notificationsCollectionRef = collection(this.firestore, `users/${userId}/notifications`);

            // Query to fetch all unread notifications
            const unreadNotificationsQuery = query(
                notificationsCollectionRef,
                where("readAt", "==", null)
            );

            // Query to fetch the latest 10 read notifications
            const readNotificationsQuery = query(
                notificationsCollectionRef,
                where("readAt", "!=", null),
                orderBy("readAt", "desc"),
                limit(30)
            );

            const unsubscribe = onSnapshot(unreadNotificationsQuery, (unreadSnapshot) => {
                const unreadNotifications = unreadSnapshot.docs.map(doc => this.mapNotification(doc));
                log.info(`Received ${unreadNotifications.length} unread notifications`);

                // Fetch read notifications separately
                getDocs(readNotificationsQuery).then(readSnapshot => {
                    const readNotifications = readSnapshot.docs.map(doc => this.mapNotification(doc));
                    log.info(`Received ${readNotifications.length} read notifications`);
                    const combinedNotifications = [...unreadNotifications, ...readNotifications];
                    callback(combinedNotifications);
                }).catch(error => {
                    log.error("Error fetching read notifications: ", error);
                    // Still call the callback with unread notifications
                    callback(unreadNotifications);
                });
            }, (error) => {
                log.error("Error listening to unread notifications: ", error);
                // Call the callback with empty array to avoid UI issues
                callback([]);
            });

            return unsubscribe;
        } catch (error) {
            log.error("Error setting up notifications listener: ", error);
            return () => {}; // Return empty function as unsubscribe
        }
    }

    /**
     * Marks all unread notifications for a user as read
     * @param userId - The user ID for which to mark notifications as read
     */
    public async markAllNotificationsAsRead(userId: number): Promise<void> {
        try {
            // Check if user is authenticated
            const isAuthenticated = await this.checkUserAuthentication();
            if (!isAuthenticated) {
                log.error("Cannot mark notifications as read: User not authenticated");
                return;
            }
            
            log.info(`Marking all notifications as read for user ID: ${userId}`);
            
            const batch = writeBatch(this.firestore);
            const notificationsCollectionRef = collection(this.firestore, `users/${userId}/notifications`);
            const unreadNotificationsQuery = query(notificationsCollectionRef, where("readAt", "==", null));

            const querySnapshot = await getDocs(unreadNotificationsQuery);
            log.info(`Found ${querySnapshot.size} unread notifications to mark as read`);
            
            if (querySnapshot.size === 0) {
                log.info("No unread notifications to mark as read");
                return;
            }
            
            querySnapshot.forEach((doc) => {
                const notificationDocRef = doc.ref;
                batch.update(notificationDocRef, { readAt: serverTimestamp() });
            });

            await batch.commit();
            log.info("Successfully marked all notifications as read");
        } catch (error) {
            log.error(`Error marking all notifications as read: ${error}`);
            throw error;
        }
    }

    /**
     * Marks a specific notification as read
     * @param userId - The user ID for which to mark the notification as read
     * @param notificationId - The ID of the notification to mark as read
     */
    public async markNotificationAsRead(userId: number, notificationId: string): Promise<void> {
        try {
            // Check if user is authenticated
            const isAuthenticated = await this.checkUserAuthentication();
            if (!isAuthenticated) {
                log.error("Cannot mark notification as read: User not authenticated");
                return;
            }
            
            log.info(`Marking notification ${notificationId} as read for user ID: ${userId}`);
            
            const notificationDocRef = doc(this.firestore, `users/${userId}/notifications/${notificationId}`);
            await updateDoc(notificationDocRef, { readAt: serverTimestamp() });
            log.info(`Successfully marked notification ${notificationId} as read`);
        } catch (error) {
            log.error(`Error marking notification as read: ${error}`);
            throw error;
        }
    }

    /**
     * Maps a Firestore document to an INotification object
     * @param doc - The Firestore document to map
     * @returns The mapped INotification object
     */
    private mapNotification(doc: QueryDocumentSnapshot<DocumentData>): INotification {
        const data = doc.data();
        const id = doc.id;
        let notification: INotification;
        
        try {
            switch (data.notificationType) {
                case NotificationType.OffersCallback:
                    notification = { 'id': id, ...data } as OfferNotification;
                    break;
                case NotificationType.PayoutStatus:
                    notification = { 'id': id, ...data } as IPayoutNotification;
                    break;
                case NotificationType.OfferWall:
                    notification = { 'id': id, ...data } as OfferWallNotification;
                    break;
                case NotificationType.InhouseTask:
                    notification = { 'id': id, ...data } as InHouseTaskNotification;
                    break;
                case NotificationType.SpinTheWheel:
                    notification = this.mapSpinTheWheelNotification(doc);
                    break;
                default:
                    notification = {
                        createdAt: data.createdAt || '',
                        notificationType: data.notificationType || '',
                        readAt: data.readAt || null,
                        userId: data.userId || 0,
                        id: id,
                        priority: data.priority || 'medium',
                        ...data,
                    };
                    break;
            }
            return notification;
        } catch (error) {
            log.error(`Error mapping notification: ${error}`);
            // Return a basic notification to avoid UI issues
            return {
                id,
                createdAt: data.createdAt || '',
                notificationType: data.notificationType || '',
                readAt: data.readAt || null,
                userId: data.userId || 0,
                priority: data.priority || 'medium',
            };
        }
    }

    /**
     * Maps a Firestore document to an ISpinTheWheelNotification object
     * @param doc - The Firestore document to map
     * @returns The mapped ISpinTheWheelNotification object
     */
    private mapSpinTheWheelNotification(doc: QueryDocumentSnapshot<DocumentData>): ISpinTheWheelNotification {
        const data = doc.data();
        const id = doc.id;
        return { 
            'id': id, 
            ...data, 
            body: { 
                ...data.body, 
                message: this.mapRewardTypeToMessage(data.body.rewardType, data.body.value) 
            } 
        } as ISpinTheWheelNotification;
    }

    /**
     * Maps a reward type to a user-friendly message
     * @param rewardType - The type of reward
     * @param value - The value of the reward
     * @returns A user-friendly message
     */
    private mapRewardTypeToMessage(rewardType: string, value: number): string {
        switch (rewardType) {
            case 'win_nothing':
                return 'Try Again!';
            case 'fixed_coins':
                return `You won some coins!`;
            case 'fixed_percentage':
                return `You won ${value}% Bonus!`;
            case 'time_gated_percentage':
                return `You won ${value}% Boost!`;
            default:
                return 'Mystery Prize!';
        }
    }

    /**
     * Cleans up resources when the service is no longer needed
     */
    public cleanup(): void {
        if (this.authStateUnsubscribe) {
            this.authStateUnsubscribe();
            this.authStateUnsubscribe = null;
        }
    }
}

// Create a singleton instance of the NotificationService
const NotificationService = new NotificationServiceClass();

// Export the singleton instance
export default NotificationService;